import { useEffect } from 'react'
import {
  useAudioNoteActiveNoteId,
  useAudioNoteMeeting,
  useAudioNoteStartTimeMs,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useAttachmentStore } from '@renderer/chat'
import { LIVE_TRANSCRIPT_PENDING_KEY } from '@renderer/chat/utils/audioNotes'
import type { MeetingViewModel } from './use-meeting-view-model'

/**
 * Scoped chat: while the Meeting View is open, the meeting rides along as a
 * context chip on the shared chat input. Past notes attach with their saved
 * transcript; live recordings attach by id only so we don't rebuild a growing
 * transcript string on every ASR delta, and the transcript is read at each
 * send. The chip outlives submits and a dismissed thread, so follow-ups carry the
 * latest transcript.
 */
export function useMeetingChatAttachment(model: MeetingViewModel) {
  const activeNoteId = useAudioNoteActiveNoteId()
  const startTimeMs = useAudioNoteStartTimeMs()
  const meeting = useAudioNoteMeeting()
  const { addAttachment, addAudioNoteAttachment, removeAttachments } = useAttachmentStore()
  const isLive = model.variant === 'live'
  const pastNote = !isLive ? model.audioNote : null

  useEffect(() => {
    if (pastNote) {
      addAudioNoteAttachment(pastNote, { viewScoped: true })
      return () => {
        removeAttachments((attachment) => {
          if (attachment.pending?.type === 'audioNote') return attachment.pending.conversationId !== pastNote.id
          if (attachment.attachment?.type !== 'audioNote') return true
          return (attachment.metadata as { id?: string } | undefined)?.id !== pastNote.id
        })
      }
    }

    if (!isLive || !activeNoteId) {
      return
    }

    const startedAt = startTimeMs !== null ? new Date(startTimeMs) : new Date()
    const id = activeNoteId

    addAttachment({
      viewScoped: true,
      attachment: {
        type: 'audioNote',
        title: `${model.title} (Live — meeting in progress)`,
        startTime: startedAt.toISOString(),
        // Materialized at send in useFinalizeAttachments — keeping this empty
        // avoids O(n) string rebuilds on every transcript delta.
        text: '',
      },
      metadata: {
        id,
        title: model.title,
        startedAt,
        endedAt: startedAt,
        userId: '',
        summary: '',
        isActive: true,
        meeting: meeting && 'app' in meeting ? meeting : undefined,
        [LIVE_TRANSCRIPT_PENDING_KEY]: true,
      },
    })

    return () => {
      removeAttachments((attachment) => {
        if (attachment.attachment?.type !== 'audioNote') return true
        return (attachment.metadata as { id?: string } | undefined)?.id !== id
      })
    }
  }, [
    pastNote,
    isLive,
    activeNoteId,
    startTimeMs,
    meeting,
    model.title,
    addAttachment,
    addAudioNoteAttachment,
    removeAttachments,
  ])
}
